import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeftRight,
  Loader2,
  UserCheck,
  ShieldAlert,
  Send,
  History,
  RefreshCw,
  X,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatUserLabel, pickParty, shortRef } from "@/lib/formatUser";
import TransferReceiptActions from "@/components/TransferReceiptActions";
import Pagination from "@/components/Pagination";

const CURRENCY_TONES = {
  USD: "bg-[#2BA8F0]",
  CAD: "bg-red-500",
  CLP: "bg-amber-500",
  DOP: "bg-blue-700",
  HTG: "bg-violet-600",
  EUR: "bg-indigo-500",
  BRL: "bg-emerald-600",
  MXN: "bg-teal-600",
};

const NO_DECIMAL = new Set(["CLP", "HTG"]);

function fmtCash(amount, currency) {
  const noDec = NO_DECIMAL.has(currency);
  const n = Number(amount || 0);
  if (noDec) return `${Math.round(n).toLocaleString("en-US")} ${currency}`;
  return `${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

const ROLE_BADGE = {
  MASTER_ADMIN: "bg-emerald-600",
  MASTER_AGENT: "bg-[#0F62FE]",
  AGENT: "bg-slate-700",
  COLLABORATOR: "bg-violet-600",
};

function RoleBadge({ role }) {
  return (
    <span
      className={`inline-flex px-1.5 py-0.5 rounded text-white text-[10px] font-bold uppercase tracking-widest ${ROLE_BADGE[role] || "bg-slate-500"}`}
    >
      {role}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Confirmation modal
// ---------------------------------------------------------------------------
function ConfirmExternalModal({ preview, note, submitting, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="external-transfer-confirm-modal"
    >
      <div className="absolute inset-0 bg-slate-900/50" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-xl p-6 animate-in zoom-in-95 duration-200">
        <button
          onClick={onCancel}
          className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
        >
          <X className="w-4 h-4" />
        </button>
        <div
          className="text-lg font-semibold text-slate-900 mb-1"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Confirm Local Currency Transfer
        </div>
        <div className="text-xs text-slate-500 mb-4">
          Please review every field. This action is atomic and recorded in the ledger.
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 mb-4 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Wallet debited</div>
            <div className="text-slate-900 font-mono text-xs">
              <span className={`px-1.5 py-0.5 rounded text-white text-[10px] mr-1 ${CURRENCY_TONES[preview.source_currency] || "bg-slate-500"}`}>{preview.source_currency}</span>
              Cash Wallet
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Wallet credited</div>
            <div className="text-slate-900 font-mono text-xs">
              <span className={`px-1.5 py-0.5 rounded text-white text-[10px] mr-1 ${CURRENCY_TONES[preview.dest_currency] || "bg-slate-500"}`}>{preview.dest_currency}</span>
              Recipient · {preview.recipient?.name || preview.recipient?.username} <RoleBadge role={preview.recipient?.role} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold">You send</div>
            <div className="font-bold text-rose-700 text-lg">− {fmtCash(preview.source_amount, preview.source_currency)}</div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Exchange rate</div>
            <div className="text-slate-700 font-mono text-xs">
              {preview.source_currency === preview.dest_currency
                ? "1:1 (same currency)"
                : `1 USD = ${preview.source_usd_rate} ${preview.source_currency} · 1 USD = ${preview.dest_usd_rate} ${preview.dest_currency}`}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Fee</div>
            <div className={`font-bold ${preview.fee_usd > 0 ? "text-orange-700" : "text-slate-400"}`} data-testid="external-confirm-fee">
              {preview.fee_usd > 0
                ? `− ${fmtCash(preview.fee_source_amount, preview.fee_source_currency)} (0.50 USD)`
                : "No fee (Master Admin)"}
            </div>
          </div>
          <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
            <div className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Recipient receives</div>
            <div className="font-bold text-emerald-700 text-lg" data-testid="external-confirm-receive">
              + {fmtCash(preview.dest_amount, preview.dest_currency)}
            </div>
          </div>
          <div className="text-[11px] text-slate-500 font-mono text-right">
            USD pivot ${Number(preview.usd_pivot_amount).toFixed(4)}
          </div>
        </div>

        {note && (
          <div className="text-xs text-slate-600 mb-4">
            <span className="font-semibold uppercase tracking-widest text-[10px]">Note</span>
            <div className="mt-1 text-slate-800">{note}</div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="external-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            data-testid="external-confirm-submit"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0043A8] disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm & send
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History table
// ---------------------------------------------------------------------------
function TransferHistory({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const { data } = await api.get(`/wallet/local-transfer/history?limit=${pageSize}&skip=${skip}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey, page, pageSize]);

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm"
      data-testid="external-transfer-history"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Local Currency Transfers</div>
            <div className="text-xs text-slate-500 mt-0.5" data-testid="external-history-total">
              {total} {total === 1 ? "transfer" : "transfers"}
            </div>
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Reload
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {[
                { k: "date", label: "Date", align: "left" },
                { k: "ref", label: "Ref", align: "left" },
                { k: "sender", label: "Sender", align: "left" },
                { k: "recipient", label: "Recipient", align: "left" },
                { k: "sent", label: "Sent", align: "right" },
                { k: "rate", label: "Rate", align: "right" },
                { k: "fee", label: "Fee (0.50 USD)", align: "right" },
                { k: "received", label: "Received", align: "right" },
                { k: "note", label: "Note", align: "left" },
                { k: "receipt", label: "Receipt", align: "left" },
              ].map((c) => (
                <th
                  key={c.k}
                  className={`bg-slate-50 ${c.align === "right" ? "text-right" : "text-left"} text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="px-5 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={10} className="px-5 py-8 text-center text-slate-500 text-sm">No transfers yet.</td></tr>
            )}
            {rows.map((r) => {
              const iAmSender = r.sender_id === user?.id;
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`external-row-${r.ref_id}`}>
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap text-xs">
                    {new Date(r.created_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-600" title={r.ref_id}>
                    {shortRef(r.ref_id, 10)}…
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-semibold text-slate-800">
                      {formatUserLabel(pickParty(r, "sender"))}
                      {iAmSender && <span className="ml-1 text-[10px] text-slate-400">(you)</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-semibold text-slate-800">
                      {formatUserLabel(pickParty(r, "recipient"))}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-rose-700 whitespace-nowrap text-right">
                    − {fmtCash(r.source_amount, r.source_currency)}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-slate-600 font-mono whitespace-nowrap text-right">
                    {r.source_currency === r.dest_currency
                      ? "1:1"
                      : `${r.source_usd_rate}/${r.dest_usd_rate}`}
                  </td>
                  <td className="px-4 py-3 font-semibold text-orange-700 whitespace-nowrap text-right" data-testid={`external-fee-${r.ref_id}`}>
                    {r.fee_usd > 0
                      ? `− ${fmtCash(r.fee_source_amount, r.fee_source_currency || r.source_currency)}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 font-semibold text-emerald-700 whitespace-nowrap text-right">
                    + {fmtCash(r.dest_amount, r.dest_currency)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-[220px] truncate" title={r.note || ""}>
                    {r.note || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <TransferReceiptActions kind="local" refId={r.ref_id} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        testId="external-history-pagination"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function SendExternalTransfer() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "MASTER_ADMIN";

  // Sender's Cash Wallet balances (source options)
  const [cashWallet, setCashWallet] = useState(null);
  // Recipient lookup state
  const [agentId, setAgentId] = useState("");
  const [email, setEmail] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [lookupErr, setLookupErr] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  // Amount + preview
  const [srcCcy, setSrcCcy] = useState("");
  const [srcAmt, setSrcAmt] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  // Load sender wallets
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/cash-wallet/me");
        setCashWallet(data);
      } catch { setCashWallet(null); }
    })();
  }, []);

  const senderWallets = useMemo(() => {
    if (!cashWallet) return [];
    if (Array.isArray(cashWallet.balances) && cashWallet.balances.length > 0) {
      return cashWallet.balances;
    }
    return [{ currency: cashWallet.currency || "USD", balance: cashWallet.balance ?? 0 }];
  }, [cashWallet]);

  const srcBalance = useMemo(() => {
    const w = senderWallets.find((b) => b.currency === srcCcy);
    return w ? Number(w.balance || 0) : 0;
  }, [senderWallets, srcCcy]);

  // Recipient lookup: debounced when both agentId & email are set
  useEffect(() => {
    if (!agentId.trim() || !email.trim()) {
      setRecipient(null);
      setLookupErr("");
      return;
    }
    let cancelled = false;
    setLookingUp(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/wallet/local-transfer/lookup", {
          agent_id: agentId.trim(),
          email: email.trim(),
        });
        if (!cancelled) {
          setRecipient(data.recipient);
          setLookupErr("");
        }
      } catch (e) {
        if (!cancelled) {
          setRecipient(null);
          setLookupErr(e.response?.data?.detail || "Recipient lookup failed");
        }
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [agentId, email]);

  // Preview effect
  useEffect(() => {
    setPreviewErr("");
    const amt = parseFloat(srcAmt);
    if (!recipient || !srcCcy || !amt || amt <= 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/wallet/local-transfer/preview", {
          recipient_agent_id: recipient.agent_code,
          recipient_email: recipient.email,
          source_currency: srcCcy,
          source_amount: amt,
        });
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setPreviewErr(e.response?.data?.detail || "Preview failed");
        }
      } finally { if (!cancelled) setPreviewing(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [recipient, srcCcy, srcAmt]);

  const totalDebit = preview ? Number(preview.source_total_debit || 0) : 0;
  const insufficient = !!srcCcy && parseFloat(srcAmt) > 0 && (preview ? totalDebit > srcBalance : parseFloat(srcAmt) > srcBalance);
  const canSubmit = !!recipient && !!srcCcy && parseFloat(srcAmt) > 0 && !insufficient && !!preview && !previewing;

  const openConfirm = () => { if (canSubmit) setConfirming(true); };

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/wallet/local-transfer/execute", {
        recipient_agent_id: recipient.agent_code,
        recipient_email: recipient.email,
        source_currency: srcCcy,
        source_amount: parseFloat(srcAmt),
        note: note.trim() || null,
      });
      toast.success(
        `Sent ${fmtCash(data.source_amount, data.source_currency)} · Recipient got ${fmtCash(data.dest_amount, data.dest_currency)}`
      );
      // Reset form
      setAgentId(""); setEmail(""); setRecipient(null);
      setSrcCcy(""); setSrcAmt(""); setNote("");
      setPreview(null); setConfirming(false);
      // Refresh sender wallets + history
      try {
        const { data: cw } = await api.get("/cash-wallet/me");
        setCashWallet(cw);
      } catch { /* ignore */ }
      setHistoryKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="send-external-transfer-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("nav.folder_my_wallet", "My Wallet")}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Transfer Funds — Local Currency
        </h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
          Send money to another AGENT or MASTER_AGENT directly from your Cash Wallet.
          Recipient is credited in their local currency using the configured exchange rate.
          {isAdmin ? " Master Admin transfers are fee-free." : " A fixed 0.50 USD fee applies per transfer."}
        </p>
      </div>

      {/* Form card */}
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-5"
        data-testid="external-transfer-form-card"
      >
        {/* Recipient lookup */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="w-4 h-4 text-[#0F62FE]" />
            <div className="text-sm font-semibold text-slate-900">1 · Recipient</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                Agent ID (agent code)
              </label>
              <input
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                data-testid="external-agent-id-input"
                placeholder="e.g. 10085"
                className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="external-email-input"
                placeholder="recipient@example.com"
                className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
              />
            </div>
          </div>
          {(lookingUp || recipient || lookupErr) && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="external-recipient-panel">
              {lookingUp && (
                <div className="flex items-center gap-2 text-slate-500 text-xs">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Verifying recipient…
                </div>
              )}
              {lookupErr && !lookingUp && (
                <div className="flex items-center gap-2 text-rose-700 text-xs" data-testid="external-recipient-error">
                  <ShieldAlert className="w-4 h-4" /> {lookupErr}
                </div>
              )}
              {recipient && !lookingUp && !lookupErr && (
                <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="external-recipient-card">
                  <div>
                    <div className="font-semibold text-slate-900">
                      {recipient.name || recipient.username}{" "}
                      <RoleBadge role={recipient.role} />
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      #{recipient.agent_code} · {recipient.email}{recipient.country ? ` · ${recipient.country}` : ""}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded text-white text-xs font-bold ${CURRENCY_TONES[recipient.cash_currency] || "bg-slate-500"}`}>
                    {recipient.cash_currency} · local currency
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Amount + Currency */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Send className="w-4 h-4 text-[#0F62FE]" />
            <div className="text-sm font-semibold text-slate-900">2 · Amount</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                From Cash Wallet
              </label>
              <select
                value={srcCcy}
                onChange={(e) => setSrcCcy(e.target.value)}
                data-testid="external-source-ccy"
                className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white"
                disabled={!recipient}
              >
                <option value="">— select currency —</option>
                {senderWallets.map((b) => (
                  <option key={b.currency} value={b.currency}>
                    {b.currency} · Cash Wallet ({fmtCash(b.balance, b.currency)})
                  </option>
                ))}
              </select>
              {srcCcy && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Balance: {fmtCash(srcBalance, srcCcy)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
                Amount
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={srcAmt}
                onChange={(e) => setSrcAmt(e.target.value)}
                data-testid="external-amount-input"
                disabled={!recipient || !srcCcy}
                placeholder="0.00"
                className={`h-10 w-full rounded-md border px-3 text-sm font-mono text-right ${
                  insufficient ? "border-rose-400 focus:ring-rose-400" : "border-slate-300 focus:ring-[#0F62FE]"
                } focus:outline-none focus:ring-2 disabled:bg-slate-100`}
              />
              {insufficient && (
                <div className="text-[11px] text-rose-600 mt-1">
                  Exceeds balance {isAdmin ? "" : "(incl. 0.50 USD fee)"}
                </div>
              )}
            </div>
            <div className="flex items-end">
              <button
                onClick={openConfirm}
                disabled={!canSubmit}
                data-testid="external-review-btn"
                className="inline-flex items-center justify-center gap-2 h-10 w-full rounded-md bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0043A8] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeftRight className="w-4 h-4" />
                Review transfer
              </button>
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Note (optional)
            </label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
              data-testid="external-note-input"
              placeholder="e.g. Weekly settlement · Feb 28"
              className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
            />
          </div>
        </div>

        {(preview || previewErr || previewing) && (
          <div
            className="rounded-md border border-slate-200 bg-slate-50/70 p-3 grid grid-cols-2 sm:grid-cols-5 gap-3"
            data-testid="external-preview"
          >
            {previewing && (
              <div className="col-span-full text-slate-500 text-xs flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Computing conversion…
              </div>
            )}
            {previewErr && (
              <div className="col-span-full text-rose-600 text-xs flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {previewErr}
              </div>
            )}
            {preview && !previewing && !previewErr && (
              <>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">You send</div>
                  <div className="text-sm font-bold text-slate-900 mt-0.5">
                    {fmtCash(preview.source_amount, preview.source_currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fee</div>
                  <div className={`text-sm font-bold mt-0.5 ${preview.fee_usd > 0 ? "text-orange-700" : "text-slate-400"}`} data-testid="external-preview-fee">
                    {preview.fee_usd > 0
                      ? fmtCash(preview.fee_source_amount, preview.fee_source_currency)
                      : "No fee"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total debit</div>
                  <div className="text-sm font-bold text-rose-700 mt-0.5" data-testid="external-preview-total-debit">
                    {fmtCash(preview.source_total_debit, preview.source_currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">USD pivot</div>
                  <div className="text-sm font-bold text-[#0F62FE] mt-0.5">
                    ${Number(preview.usd_pivot_amount).toFixed(4)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Recipient gets</div>
                  <div className="text-sm font-bold text-emerald-700 mt-0.5" data-testid="external-preview-received">
                    {fmtCash(preview.dest_amount, preview.dest_currency)}
                  </div>
                </div>
                <div className="col-span-full text-[11px] text-slate-500 font-mono">
                  {preview.source_currency === preview.dest_currency
                    ? "Same-currency 1:1"
                    : `1 USD = ${preview.source_usd_rate} ${preview.source_currency} · 1 USD = ${preview.dest_usd_rate} ${preview.dest_currency}`}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {confirming && preview && (
        <ConfirmExternalModal
          preview={preview}
          note={note}
          submitting={submitting}
          onCancel={() => setConfirming(false)}
          onConfirm={submit}
        />
      )}

      <TransferHistory refreshKey={historyKey} />
    </div>
  );
}
